import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from 'urql';
import { useNavigate } from 'react-router-dom';
import { Q_NOTIFICATIONS, M_MARK_NOTIF_READ, M_MARK_ALL_NOTIF_READ } from '@/lib/queries';

const KIND_ICON: Record<string, string> = {
  'post.published': '✅', 'post.failed': '❌', 'automation.review': '📝',
};

function timeAgo(iso: string) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function NotificationBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Poll every 30s so new events surface without a refresh.
  const [{ data }, refetch] = useQuery({ query: Q_NOTIFICATIONS, variables: { limit: 20 } });
  const [, markRead] = useMutation(M_MARK_NOTIF_READ);
  const [, markAll] = useMutation(M_MARK_ALL_NOTIF_READ);

  const list: any[] = data?.notifications ?? [];
  const unread = data?.unreadNotificationCount ?? 0;

  useEffect(() => {
    const id = setInterval(() => refetch({ requestPolicy: 'network-only' }), 30_000);
    return () => clearInterval(id);
  }, [refetch]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const openItem = async (n: any) => {
    if (!n.read) { await markRead({ id: n.id }); refetch({ requestPolicy: 'network-only' }); }
    if (n.link) { setOpen(false); nav(n.link); }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setOpen((o) => !o); if (!open) refetch({ requestPolicy: 'network-only' }); }}
        className="relative w-9 h-9 rounded-lg hover:bg-stone-100 flex items-center justify-center text-stone-600" aria-label="Notifications">
        <span className="text-lg">🔔</span>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-semibold rounded-full flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-stone-200 rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-stone-100">
            <span className="text-sm font-semibold text-stone-900">Notifications</span>
            {unread > 0 && (
              <button onClick={async () => { await markAll({}); refetch({ requestPolicy: 'network-only' }); }}
                className="text-[11px] text-violet-600 hover:underline">Mark all read</button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {list.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-stone-400">You're all caught up.</p>
            ) : list.map((n) => (
              <button key={n.id} onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 border-b border-stone-50 hover:bg-stone-50 flex gap-3 ${n.read ? '' : 'bg-violet-50/40'}`}>
                <span className="text-base flex-shrink-0">{KIND_ICON[n.kind] ?? '•'}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-stone-800 font-medium truncate">{n.title}</p>
                  {n.body && <p className="text-[11px] text-stone-500 line-clamp-2">{n.body}</p>}
                  <span className="text-[10px] text-stone-400">{timeAgo(n.createdAt)}</span>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-violet-600 flex-shrink-0 mt-1.5" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
